import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BN from 'bn.js';

import { Button, ButtonWrapper } from 'components/common/button/button';
import { useAppDispatch, useAppSelector } from 'redux/use-hooks';
import { RegisterContracts } from 'types';

import {
  selectAccountId,
  selectRegisterContracts,
  actions as activeAccountActions
} from 'redux/slices/account';
const { actionAccountCheckBalance } = activeAccountActions;

import FungibleTokens from 'services/fungible-tokens';

import { formatTokenAmount } from 'utils/amounts';
import { BALANCES_PAGE } from 'site-settings/site-navigation';
import { useModal } from '../context';
import {
  createFunctionCallAction,
  isWalletCancelError,
  wallet
} from 'utils/wallet';

import config from 'config';
const {
  OPN_CONTRACT,
  OCC_CONTRACT,
  USDC_CONTRACT,
  METHODS,
  FT_STORAGE_DEPOSIT_GAS
} = config;
import { actions as statusSliceActions } from 'redux/slices/status';
const { showCustomAlert } = statusSliceActions;
import {
  StyledContainer,
  StyledSection,
  StyledHeading,
  StyledDesc
} from './views.style';

const RegisterView = () => {
  const [loading, setLoading] = useState(false);
  const [storageBalance, setStorageBalance] = useState('0');
  const accountId = useAppSelector(selectAccountId);
  const registerContracts = useAppSelector(
    selectRegisterContracts
  ) as RegisterContracts;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openModal, closeModal } = useModal();

  useEffect(() => {
    const isRegistered = Object.entries(registerContracts).every(
      ([, contract]) => contract.value
    );
    if (accountId && isRegistered) {
      closeModal();
      navigate(BALANCES_PAGE);
    }
  }, [accountId, registerContracts, closeModal, navigate]);

  useEffect(() => {
    (async function () {
      try {
        const pendingContracts = Object.entries(registerContracts).filter(
          ([, { value }]) => !value
        );

        if (!pendingContracts.length) {
          setStorageBalance('0');
          return;
        }

        let amountTogether = new BN('0');
        for (const [contractName, { min }] of pendingContracts) {
          if (min) {
            amountTogether = amountTogether.add(new BN(min));
            continue;
          }
          const bounds = await FungibleTokens.getViewMethod({
            contractName,
            accountId: 'dontcare',
            methodName: METHODS.storage_balance_bounds
          });
          amountTogether = amountTogether.add(new BN(bounds.min));
        }

        setStorageBalance(amountTogether.toString());
      } catch (e) {
        console.warn(`Failed to load FT for ${OCC_CONTRACT}`, e);
      }
    })();
  }, [registerContracts]);

  const onClickHandler = async () => {
    if (!accountId) {
      openModal('ACCESS_VIEW');
      return;
    }

    setLoading(true);
    try {
      const pendingTransactions = Object.entries(registerContracts)
        .filter(([contractName, { value, min }]) => contractName && !value && min)
        .map(([contractName, { min }]) => ({
          receiverId: contractName,
          actions: [
            createFunctionCallAction(
              METHODS.storage_deposit,
              {
                account_id: accountId,
                registration_only: true
              },
              FT_STORAGE_DEPOSIT_GAS,
              min
            )
          ]
        }));

      if (pendingTransactions.length) {
        await wallet.signAndSendTransactions(pendingTransactions);
      }

      await Promise.all(
        [OPN_CONTRACT, OCC_CONTRACT, USDC_CONTRACT]
          .filter(Boolean)
          .map(async (contractName) => {
            await dispatch(
              actionAccountCheckBalance({ contractName, accountId })
            );
          })
      );
      setLoading(false);
      closeModal();
      navigate(BALANCES_PAGE);
    } catch (e) {
      setLoading(false);
      if (isWalletCancelError(e)) {
        return;
      }
      closeModal();
      dispatch(
        showCustomAlert({
          showAlert: true,
          success: false,
          messageCodeHeader: 'Registration failed',
          messageCode:
            'Please ensure your NEAR ID meets the minimum balance requirement'
        })
      );
      console.warn(`Failed to transferStorageDeposit`, e);
    }
  };

  return (
    <StyledContainer>
      <StyledSection p="35px">
        <StyledHeading fontWeight="bold">Register NEAR ID</StyledHeading>
        <StyledDesc mb="60px" textAlign="center" lineHeight={2} fontSize={16}>
          {`To finish setting up your OFP wallet, you must register your NEAR ID.
          Please ensure that you have ${
            new BN(storageBalance).isZero()
              ? '0.00000'
              : formatTokenAmount(storageBalance, 24, 5)
          } NEAR in your account before registering.`}
        </StyledDesc>
        <ButtonWrapper>
          <Button
            fullwidth
            disabled={loading}
            loading={loading}
            onClick={onClickHandler}
          >
            Register
          </Button>
        </ButtonWrapper>
      </StyledSection>
    </StyledContainer>
  );
};

export default RegisterView;
